// W36: thin wrapper over the generated billing client.
// W15: typed access to /v1/billing/subscriptions.
//
// Types come from `./generated/billing` (the same document as `./billing.ts`;
// regenerate with `pnpm --filter @repletoryreach/sdk-api generate billing`);
// the request path stays on the house core so the envelope, Idempotency-Key,
// token refresh and ApiError mapping behave exactly as before.
//
// `services/billing/Program.cs` serialises with
// `JsonIgnoreCondition.WhenWritingNull`, so a field declared `null | T` is
// normally **omitted** from the JSON rather than sent as `null`. W46 carved out
// the two places where that was a lie: `GET /v1/billing/subscriptions/me` and
// the `proration` of a same-plan PATCH now pin their key with
// `[JsonIgnore(Condition = Never)]`, so both really do read back as `null`.
//
// Money is integer minor units. `Plan.monthly_amount_minor` and
// `Plan.included_credits_minor` are C# `int` (the ledger/wallet amounts over
// in `./billing.ts` are `long`): both land as `number` either way.

import { http } from './_core/request.js';
import type { Envelope } from './_core/envelope.js';
import type * as gen from './generated/billing/index.js';

// ── Types (generated) ──────────────────────────────────────────────────────

/** Mirrors `SubscriptionStatuses` in `services/billing/Domain/Billing.cs`. */
export type SubscriptionStatus = 'active' | 'cancelled' | 'past_due';

/**
 * A subscription row (the tenant and admin projections are identical).
 *
 * ⚠ DRIFT: the generated `SubscriptionDto.status` is an unconstrained
 * `string`; the union is pinned here because the consoles switch on it.
 */
export type Subscription = Omit<gen.SubscriptionDto, 'status'> & {
  status: SubscriptionStatus;
  /** A cheaper plan chosen this month, charged from `billing_cycle_end` (11 October 2026). */
  pending_plan_id?: string | null;
  /** The saved card the plan is charged to. */
  payment_method_id?: string | null;
};

export type Plan = gen.PlanDto;
export type Proration = gen.ProrationDto;
/** `return_url`: where PayFast sends the customer back to when the plan is paid with a new card (always the console). */
export interface CreateSubscriptionRequest { plan_code: string; return_url?: string }
export interface UpdateSubscriptionRequest { plan_code: string; return_url?: string }

/**
 * Plans are paid by card (11 October 2026). With no card saved yet, subscribing or upgrading answers **202** with
 * this: send the customer to `checkout_url` (PayFast), which saves the card and takes the charge; the plan starts,
 * or the upgrade applies, when PayFast confirms it. A declined saved card is 402 `CARD_DECLINED`.
 */
export interface CardCheckout {
  card_required: true;
  checkout_url: string;
  intent_id: string;
  amount_minor: number;
  currency: string;
}

export function isCardCheckout(x: unknown): x is CardCheckout {
  return typeof x === 'object' && x !== null && (x as CardCheckout).card_required === true && typeof (x as CardCheckout).checkout_url === 'string';
}

/** The card a plan is charged to. PayFast does not share card digits, so `label` may read "your card saved with PayFast". */
export interface SavedCard {
  id: string;
  provider: string;
  label: string;
  brand?: string | null;
  last4?: string | null;
  exp_month?: number | null;
  exp_year?: number | null;
  added_at: string;
}

/**
 * `PATCH /v1/billing/subscriptions/me` - **not** an `Envelope`: `proration`
 * sits beside `data` at the top level (generated as
 * `SubscriptionUpdateResponse`).
 *
 * `proration` is `Proration | null`: W46 pinned the key so a same-plan PATCH
 * really does answer `"proration": null` instead of dropping it. Checking it
 * for falsiness still works and remains the safest read against an older
 * service.
 */
export type UpdateResult = Omit<gen.SubscriptionUpdateResponse, 'data'> & {
  data: Subscription;
};

// ── Operations ─────────────────────────────────────────────────────────────

/**
 * What taking a plan now charges (4 October 2026: plans are charged on the 1st of each month).
 * `charge_now_minor` and `credits_now_minor` are the share of this month that is left (the whole month
 * when taken on the 1st, `full_month`); then `monthly_amount_minor` with `included_credits_minor` on every
 * 1st from `next_charge_at`. Hand-written until the billing client is regenerated.
 */
export interface SubscriptionQuote {
  plan_code: string;
  currency: string;
  charge_now_minor: number;
  credits_now_minor: number;
  full_month: boolean;
  period_start: string;
  next_charge_at: string;
  monthly_amount_minor: number;
  included_credits_minor: number;
}

/** 400 `UNKNOWN_PLAN` when no active plan has that code. */
export function quote(planCode: string): Promise<Envelope<SubscriptionQuote>> {
  return http.get<Envelope<SubscriptionQuote>>(`/v1/billing/subscriptions/quote?plan_code=${encodeURIComponent(planCode)}`);
}

export function listPlans(): Promise<Envelope<Plan[]>> {
  return http.get<Envelope<Plan[]>>('/v1/billing/subscriptions/plans');
}

/**
 * The caller's active (or `past_due`) subscription; `cancelled` stays hidden.
 *
 * W46: with no subscription the body is `{"data": null}` (it used to be `{}`,
 * which made `unwrap()` yield `undefined`), so `unwrap(await getMine())` is
 * `null`. Checking the result for falsiness still works and remains the safest
 * read against an older service.
 */
export function getMine(): Promise<Envelope<Subscription | null>> {
  return http.get<Envelope<Subscription | null>>('/v1/billing/subscriptions/me');
}

/**
 * ⚠ DRIFT: answers **201 Created**, not 200 (409 `ALREADY_SUBSCRIBED`, 400 `UNKNOWN_PLAN`). The first
 * charge covers the rest of this month (see `quote`); the cycle then runs from one 1st to the next.
 */
export function create(req: CreateSubscriptionRequest): Promise<Envelope<Subscription | CardCheckout>> {
  return http.post<Envelope<Subscription | CardCheckout>>('/v1/billing/subscriptions', { body: req });
}

/**
 * A cheaper paid plan takes effect at the next renewal (`pending_plan_id`); an upgrade is charged to the card now,
 * with whatever extra use owes this month (30 October 2026). 409 `EXTRA_USE_PENDING` while a charge for extra use is
 * still being confirmed (try again in a few minutes); 402 `CARD_DECLINED` when the card is declined.
 */
export function update(req: UpdateSubscriptionRequest): Promise<UpdateResult | Envelope<CardCheckout>> {
  return http.patch<UpdateResult | Envelope<CardCheckout>>('/v1/billing/subscriptions/me', { body: req });
}

/** The card plans are charged to, or null when none is saved. */
export function getCard(): Promise<Envelope<{ card: SavedCard | null }>> {
  return http.get<Envelope<{ card: SavedCard | null }>>('/v1/billing/card');
}

/**
 * Add or replace the card: a small PayFast payment (it goes into the bought credit) that saves the card. Answers
 * 202 with where to send the customer. A plan whose card was declined is tried again on the new card.
 */
export function addCard(returnUrl?: string): Promise<Envelope<CardCheckout>> {
  return http.post<Envelope<CardCheckout>>('/v1/billing/card', { body: returnUrl ? { return_url: returnUrl } : {} });
}

/**
 * Cancel the plan. It stays until the end of the month already paid for (`ends_at`) and is not charged
 * again; the organisation then moves to the entry plan. A past-due plan ends at once.
 */
export function cancel(): Promise<void> {
  return http.delete<void>('/v1/billing/subscriptions/me');
}

/** "Keep my plan": withdraw a cancellation before the plan ends (404 `NOTHING_TO_RESUME` when there is none). */
export function resume(): Promise<Envelope<Subscription>> {
  return http.post<Envelope<Subscription>>('/v1/billing/subscriptions/me/resume');
}
